import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api.ts";
import { DeleteConfirm } from "../DeleteConfirm.tsx";
import { ErrorBanner } from "../ErrorBanner.tsx";
import { Pagination } from "../Pagination.tsx";
import { useCommunity } from "../community.ts";
import type { Restaurant, RestaurantStatus } from "../types.ts";
import { RestaurantForm } from "./RestaurantForm.tsx";

const PAGE_SIZE = 20;

const STATUS_FILTERS: { value: "" | RestaurantStatus; label: string }[] = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "private", label: "Private" },
  { value: "rejected", label: "Rejected" },
  { value: "hidden", label: "Hidden" },
];

interface Props {
  // Pre-seeds the status filter so the legacy "Pending restaurants" tab
  // keeps landing on pending by default. Empty string = any status.
  defaultStatus?: "" | RestaurantStatus;
}

export function AllRestaurants({ defaultStatus = "" }: Props) {
  const community = useCommunity();
  const [status, setStatus] = useState<"" | RestaurantStatus>(defaultStatus);
  const [items, setItems] = useState<Restaurant[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Restaurant | null>(null);
  const [deleting, setDeleting] = useState<Restaurant | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      if (community) params.set("community_id", community);
      if (status) params.set("status", status);
      const data = await api<{ items: Restaurant[]; total: number }>(
        `/admin/restaurants?${params.toString()}`,
      );
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [offset, community, status]);

  useEffect(() => {
    setOffset(0);
  }, [community, status]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(
    id: string,
    action: "approve" | "reject" | "hide" | "unhide",
  ) {
    setBusy(`${id}-${action}`);
    try {
      await api(
        `/admin/restaurants/${encodeURIComponent(id)}/${action}`,
        { method: "POST" },
      );
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="view-head">
        <h2>
          {defaultStatus === "pending" ? "Pending restaurants" : "All restaurants"}
        </h2>
        <span className="view-meta">
          {defaultStatus === "pending"
            ? "Review and approve restaurants awaiting moderation."
            : "Browse every restaurant across selected scope."}
        </span>
      </div>
      <div className="filter-row">
        <label htmlFor="restaurant-status-filter" className="filter-label">
          Status
        </label>
        <select
          id="restaurant-status-filter"
          className="luna-input luna-input-sm"
          value={status}
          onChange={(e) => setStatus(e.target.value as "" | RestaurantStatus)}
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s.value || "all"} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <div style={{ marginLeft: "auto" }}>
          <button
            type="button"
            className="luna-btn luna-btn-primary luna-btn-sm"
            onClick={() => setCreating(true)}
            disabled={!community}
            title={
              community
                ? "Add a new restaurant to this community"
                : "Pick a community to add a restaurant"
            }
          >
            + Add restaurant
          </button>
        </div>
      </div>

      {!community && (
        <div className="community-required">
          Pick a community above to add a new restaurant.
        </div>
      )}

      {error && <ErrorBanner code={error} />}

      {loading && items.length === 0 ? (
        <div className="spinner-block">Loading restaurants…</div>
      ) : items.length === 0 ? (
        <div className="card">
          <div className="empty">
            <strong>No restaurants match this filter</strong>
            <span>Try changing the status filter or community scope.</span>
          </div>
        </div>
      ) : (
        items.map((r) => {
          const menu = (r.menuItems ?? []).slice(0, 8);
          const more = (r.menuItems ?? []).length - menu.length;
          const isPending = r.status === "pending";
          const isHidden = r.status === "hidden";
          const isRejected = r.status === "rejected";
          return (
            <div key={r.id} className="card">
              <h2>
                {r.name}{" "}
                <span
                  className={`luna-badge luna-badge-${
                    r.status === "approved"
                      ? "success"
                      : r.status === "pending"
                        ? "warning"
                        : r.status === "hidden"
                          ? "error"
                          : "neutral"
                  }`}
                >
                  {r.status}
                </span>
              </h2>
              <p className="meta">
                Restaurant #{r.id} · community #{r.communityId} · owner user
                #{r.ownerUserId}
                {r.parentRestaurantId && (
                  <> · revision of #{r.parentRestaurantId}</>
                )}
              </p>
              <p className="meta">
                WhatsApp: {r.whatsappNumber} · Hours: {r.hours}
              </p>
              <pre className="body">{r.info}</pre>
              {menu.length > 0 && (
                <>
                  <h3>Menu</h3>
                  <ul className="menu-list">
                    {menu.map((m) => (
                      <li key={m.id}>
                        <span>
                          {m.nameEn}
                          {m.nameEs && <> · {m.nameEs}</>}
                        </span>
                        <span className="price">{m.price}</span>
                      </li>
                    ))}
                  </ul>
                  {more > 0 && (
                    <p className="meta">+ {more} more menu items</p>
                  )}
                </>
              )}
              <div className="row-actions">
                {isPending && (
                  <>
                    <button
                      type="button"
                      className="luna-btn luna-btn-primary"
                      onClick={() => void act(r.id, "approve")}
                      disabled={busy !== null}
                    >
                      {busy === `${r.id}-approve` ? "Approving…" : "Approve"}
                    </button>
                    <button
                      type="button"
                      className="luna-btn luna-btn-outline"
                      onClick={() => void act(r.id, "reject")}
                      disabled={busy !== null}
                    >
                      {busy === `${r.id}-reject` ? "Rejecting…" : "Reject"}
                    </button>
                  </>
                )}
                {!isHidden && !isRejected && (
                  <button
                    type="button"
                    className="luna-btn luna-btn-ghost"
                    onClick={() => void act(r.id, "hide")}
                    disabled={busy !== null}
                  >
                    {busy === `${r.id}-hide` ? "Hiding…" : "Hide"}
                  </button>
                )}
                {(isHidden || isRejected) && (
                  <button
                    type="button"
                    className="luna-btn luna-btn-outline"
                    onClick={() => void act(r.id, "unhide")}
                    disabled={busy !== null}
                    title={
                      isRejected
                        ? "Restore to pending (re-enters approval queue)"
                        : "Restore to pending"
                    }
                  >
                    {busy === `${r.id}-unhide`
                      ? "Restoring…"
                      : isRejected
                        ? "Restore"
                        : "Unhide"}
                  </button>
                )}
                <button
                  type="button"
                  className="luna-btn luna-btn-outline"
                  onClick={() => setEditing(r)}
                  disabled={busy !== null}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="luna-btn luna-btn-ghost"
                  onClick={() => setDeleting(r)}
                  disabled={busy !== null}
                  style={{ color: "var(--red)" }}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })
      )}
      <Pagination
        total={total}
        limit={PAGE_SIZE}
        offset={offset}
        onChange={setOffset}
        loading={loading}
        label="Restaurants"
      />
      {creating && community && (
        <RestaurantForm
          communityId={community}
          initial={null}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void load();
          }}
        />
      )}
      {editing && (
        <RestaurantForm
          communityId={editing.communityId}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
      {deleting && (
        <DeleteConfirm
          title={`Delete restaurant #${deleting.id}`}
          description={`Delete “${deleting.name}” and all of its menu items?`}
          endpoint={`/admin/restaurants/${encodeURIComponent(deleting.id)}`}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void load();
          }}
        />
      )}
    </>
  );
}
